import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { 
  FiArrowRight,
  FiX
} from 'react-icons/fi'
import {
  HiUserGroup,
  HiDocumentText,
  HiPhotograph,
  HiCalendar,
  HiExternalLink,
  HiLightningBolt,
  HiCheckCircle,
  HiExclamationCircle,
  HiQuestionMarkCircle
} from 'react-icons/hi'

const AdminDashboard: React.FC = () => {
  const [showHelp, setShowHelp] = useState(false)
  const tips = [
    {
      icon: HiLightningBolt,
      title: 'Save to Update',
      description: 'Click the Save button after making edits. Once saved, your changes appear instantly on your live website.'
    },
    {
      icon: HiCheckCircle,
      title: 'Easy to Fix',
      description: 'Made a mistake? Simply edit the content again and save. You can update anything as many times as needed.'
    },
    {
      icon: HiExclamationCircle,
      title: 'Check on Mobile',
      description: 'Your site automatically adapts to all screen sizes. After saving, view your site on your phone to see how it looks.'
    }
  ]

  const quickActions = [
    {
      title: 'Add New Artist',
      description: 'Add a new musician to your roster',
      icon: HiUserGroup,
      link: '/admin/artists/add',
      gradient: 'from-blue-500/50 to-purple-500/50',
      bgGradient: 'from-blue-50 to-purple-50'
    },
    {
      title: 'Edit Content',
      description: 'Update hero, about, or contact sections',
      icon: HiDocumentText,
      link: '/admin/content',
      gradient: 'from-purple-500/50 to-indigo-500/50',
      bgGradient: 'from-purple-50 to-indigo-50'
    },
    {
      title: 'Manage Media',
      description: 'Upload and organize images',
      icon: HiPhotograph,
      link: '/admin/media',
      gradient: 'from-indigo-500/50 to-blue-500/50',
      bgGradient: 'from-indigo-50 to-blue-50'
    }
  ]

  const recentActivity = [
    { action: 'Added new artist', item: 'Luna Rodriguez', time: '2 hours ago' },
    { action: 'Updated content', item: 'Hero section', time: '1 day ago' },
    { action: 'Uploaded image', item: 'artist-photo-3.jpg', time: '2 days ago' },
    { action: 'Featured artist', item: 'The Midnight Collective', time: '3 days ago' }
  ]

  return (
    <div className="space-y-6 relative">
      {/* Background Gradient Flares */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-br from-blue-200/80 to-purple-200/80 rounded-full blur-3xl -z-10 pointer-events-none"></div>
      <div className="absolute bottom-20 left-20 w-[500px] h-[500px] bg-gradient-to-tr from-purple-200/65 to-blue-200/65 rounded-full blur-3xl -z-10 pointer-events-none"></div>
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-neutral-900 mb-1">Dashboard</h1>
          <p className="text-neutral-600 text-sm">Welcome back. Here's an overview of your site.</p>
        </div>
        <button
          onClick={() => setShowHelp(!showHelp)}
          className="flex items-center justify-center space-x-2 px-4 py-2.5 bg-white border border-neutral-200 text-neutral-700 rounded-lg hover:bg-neutral-50 transition-colors text-sm font-medium w-full sm:w-auto"
        >
          <HiQuestionMarkCircle size={18} />
          <span>Help</span>
        </button>
      </div>

      {/* Help Tips (Collapsible) */}
      {showHelp && (
        <div className="p-[1px] bg-gradient-to-r from-indigo-500/50 via-blue-500/50 to-cyan-500/50 rounded-lg mb-6">
          <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-neutral-900">How This Works</h2>
              <button
                onClick={() => setShowHelp(false)}
                className="text-neutral-500 hover:text-neutral-700 transition-colors p-1.5 hover:bg-white/50 rounded-lg"
              >
                <FiX size={20} />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {tips.map((tip, index) => (
                <div key={index} className="flex space-x-3">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center shadow-md">
                      <tip.icon size={18} className="text-white" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-neutral-900 text-sm mb-1">{tip.title}</h3>
                    <p className="text-xs text-neutral-700 leading-relaxed">{tip.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="p-[1px] bg-gradient-to-r from-blue-500/50 via-purple-500/50 to-indigo-500/50 rounded-lg">
        <div className="bg-white rounded-lg p-6">
          <h2 className="text-lg font-semibold text-neutral-900 mb-5 flex items-center space-x-2">
            <div className="p-2 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg shadow-sm">
              <HiLightningBolt size={18} className="text-white" />
            </div>
            <span>Quick Actions</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {quickActions.map((action, index) => (
              <div key={index} className={`p-[1px] bg-gradient-to-br ${action.gradient} rounded-lg group hover:shadow-lg transition-all duration-200`}>
                <Link
                  to={action.link}
                  className="relative block p-5 bg-white rounded-lg h-full transition-all duration-200 group-hover:bg-gradient-to-br group-hover:from-white group-hover:to-neutral-50"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className={`p-3 bg-gradient-to-br ${action.gradient} rounded-xl shadow-md group-hover:shadow-lg transition-all`}>
                      <action.icon size={26} className="text-white" />
                    </div>
                    <FiArrowRight size={18} className="text-neutral-400 group-hover:text-neutral-700 group-hover:translate-x-0.5 transition-all" />
                  </div>
                  <h3 className="font-semibold text-sm text-neutral-900 mb-1.5">{action.title}</h3>
                  <p className="text-xs text-neutral-600 leading-relaxed">{action.description}</p>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="p-[1px] bg-gradient-to-br from-purple-500/50 to-blue-500/50 rounded-lg">
        <div className="bg-white rounded-lg p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-semibold text-neutral-900 flex items-center space-x-2">
              <div className="p-2 bg-gradient-to-br from-purple-500 to-blue-500 rounded-lg shadow-sm">
                <HiCalendar size={18} className="text-white" />
              </div>
              <span>Recent Activity</span>
            </h2>
          </div>
          <div className="space-y-2">
            {recentActivity.map((activity, index) => (
              <div
                key={index}
                className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gradient-to-br hover:from-purple-50/30 hover:to-blue-50/30 transition-all duration-200"
              >
                <div className="w-2 h-2 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full mt-1.5 flex-shrink-0"></div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-neutral-900">
                    <span className="font-medium">{activity.action}</span>
                    {' '}
                    <span className="text-neutral-600">{activity.item}</span>
                  </p>
                  <p className="text-xs text-neutral-500 mt-0.5">{activity.time}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Site Status */}
      <div className="p-[1px] bg-gradient-to-r from-indigo-500/50 via-purple-500/50 to-blue-500/50 rounded-lg">
        <div className="bg-gradient-to-br from-neutral-900 to-neutral-800 text-white rounded-lg p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-semibold flex items-center space-x-2">
              <div className="p-2 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-lg shadow-lg shadow-indigo-500/30">
                <HiExternalLink size={18} className="text-white" />
              </div>
              <span>Site Status</span>
            </h2>
            <div className="flex items-center space-x-2 px-3 py-1.5 bg-emerald-500/20 rounded-full border border-emerald-500/30">
              <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse shadow-lg shadow-emerald-400/50"></div>
              <span className="text-sm font-medium text-emerald-400">Live</span>
            </div>
          </div>
          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <p className="font-medium text-white mb-1">Your site is running</p>
                <p className="text-sm text-neutral-300">Last deployed 2 days ago</p>
              </div>
              <a
                href="/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center space-x-2 bg-gradient-to-r from-blue-400 to-purple-400 hover:from-blue-500 hover:to-purple-500 text-white transition-all text-sm font-semibold px-4 py-2 rounded-md w-full sm:w-auto shadow-lg hover:shadow-xl"
              >
                <span>View Site</span>
                <HiExternalLink size={14} />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AdminDashboard
